/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Error } from '../models/error';
import { ReportCatalog } from '../models/report-catalog';
import { ReportRecipientList } from '../models/report-recipient-list';
import { ReportRunList } from '../models/report-run-list';
import { ReportTestRunAccepted } from '../models/report-test-run-accepted';


/**
 * Corona's business reports: the legacy CSV invoice exports and the
 * scheduled XLSX reports, their email recipients, test runs and the
 * archive of scheduled runs.
 */
@Injectable({
  providedIn: 'root',
})
export class ReportsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listReports
   */
  static readonly ListReportsPath = '/reports';

  /**
   * List the report catalog.
   *
   * Returns the five business reports in the order the Console shows
   * them, each with its format, the kind of period it takes, the period
   * a scheduled run would report on right now (`default_period`) and,
   * for a scheduled report, a description of its schedule. The catalog
   * is fixed in code; it carries no recipients and no archive rows.
   * Authentication only, like every other read.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listReports()` instead.
   *
   * This method doesn't expect any request body.
   */
  listReports$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ReportCatalog>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.ListReportsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportCatalog>;
      })
    );
  }

  /**
   * List the report catalog.
   *
   * Returns the five business reports in the order the Console shows
   * them, each with its format, the kind of period it takes, the period
   * a scheduled run would report on right now (`default_period`) and,
   * for a scheduled report, a description of its schedule. The catalog
   * is fixed in code; it carries no recipients and no archive rows.
   * Authentication only, like every other read.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listReports$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listReports(params?: {
    context?: HttpContext
  }
): Observable<ReportCatalog> {

    return this.listReports$Response(params).pipe(
      map((r: StrictHttpResponse<ReportCatalog>) => r.body as ReportCatalog)
    );
  }

  /**
   * Path part for operation downloadReport
   */
  static readonly DownloadReportPath = '/reports/{report_id}/download';

  /**
   * Generate a report from current data and download it.
   *
   * Generates the report synchronously from current data and streams it
   * back as an attachment named `<report-id>-<suffix>.<ext>`
   * (`Content-Disposition: attachment; filename="…"`). The legacy CSV
   * exports (`laskut`, `laskut-kaikki-tiedot`) are
   * `text/csv; charset=windows-1252`; the others are XLSX workbooks.
   *
   * `period` is required, and only accepted, for a report whose
   * `period_kind` is not `none`: `YYYY-MM` for `month`, `YYYY-MM-DD` for
   * `date`. A missing, malformed or superfluous period is 400. An
   * unknown report id is 404.
   *
   * A failure before the first byte is a normal JSON error without the
   * attachment header. A failure after the file has started streaming
   * aborts the response, so the browser sees a failed download rather
   * than a truncated file.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadReport$Csv()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadReport$Csv$Response(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;

    /**
     * The report&#x27;s period: &#x60;YYYY-MM&#x60; for a &#x60;month&#x60; report, &#x60;YYYY-MM-DD&#x60;
     * for a &#x60;date&#x60; report, absent for a &#x60;none&#x60; report.
     */
    period?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.DownloadReportPath, 'get');
    if (params) {
      rb.path('report_id', params.report_id, {});
      rb.query('period', params.period, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'text/csv',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Generate a report from current data and download it.
   *
   * Generates the report synchronously from current data and streams it
   * back as an attachment named `<report-id>-<suffix>.<ext>`
   * (`Content-Disposition: attachment; filename="…"`). The legacy CSV
   * exports (`laskut`, `laskut-kaikki-tiedot`) are
   * `text/csv; charset=windows-1252`; the others are XLSX workbooks.
   *
   * `period` is required, and only accepted, for a report whose
   * `period_kind` is not `none`: `YYYY-MM` for `month`, `YYYY-MM-DD` for
   * `date`. A missing, malformed or superfluous period is 400. An
   * unknown report id is 404.
   *
   * A failure before the first byte is a normal JSON error without the
   * attachment header. A failure after the file has started streaming
   * aborts the response, so the browser sees a failed download rather
   * than a truncated file.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `downloadReport$Csv$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadReport$Csv(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;

    /**
     * The report&#x27;s period: &#x60;YYYY-MM&#x60; for a &#x60;month&#x60; report, &#x60;YYYY-MM-DD&#x60;
     * for a &#x60;date&#x60; report, absent for a &#x60;none&#x60; report.
     */
    period?: string;
    context?: HttpContext
  }
): Observable<Blob> {

    return this.downloadReport$Csv$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Generate a report from current data and download it.
   *
   * Generates the report synchronously from current data and streams it
   * back as an attachment named `<report-id>-<suffix>.<ext>`
   * (`Content-Disposition: attachment; filename="…"`). The legacy CSV
   * exports (`laskut`, `laskut-kaikki-tiedot`) are
   * `text/csv; charset=windows-1252`; the others are XLSX workbooks.
   *
   * `period` is required, and only accepted, for a report whose
   * `period_kind` is not `none`: `YYYY-MM` for `month`, `YYYY-MM-DD` for
   * `date`. A missing, malformed or superfluous period is 400. An
   * unknown report id is 404.
   *
   * A failure before the first byte is a normal JSON error without the
   * attachment header. A failure after the file has started streaming
   * aborts the response, so the browser sees a failed download rather
   * than a truncated file.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadReport$VndOpenxmlformatsOfficedocumentSpreadsheetmlSheet()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadReport$VndOpenxmlformatsOfficedocumentSpreadsheetmlSheet$Response(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;

    /**
     * The report&#x27;s period: &#x60;YYYY-MM&#x60; for a &#x60;month&#x60; report, &#x60;YYYY-MM-DD&#x60;
     * for a &#x60;date&#x60; report, absent for a &#x60;none&#x60; report.
     */
    period?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.DownloadReportPath, 'get');
    if (params) {
      rb.path('report_id', params.report_id, {});
      rb.query('period', params.period, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Generate a report from current data and download it.
   *
   * Generates the report synchronously from current data and streams it
   * back as an attachment named `<report-id>-<suffix>.<ext>`
   * (`Content-Disposition: attachment; filename="…"`). The legacy CSV
   * exports (`laskut`, `laskut-kaikki-tiedot`) are
   * `text/csv; charset=windows-1252`; the others are XLSX workbooks.
   *
   * `period` is required, and only accepted, for a report whose
   * `period_kind` is not `none`: `YYYY-MM` for `month`, `YYYY-MM-DD` for
   * `date`. A missing, malformed or superfluous period is 400. An
   * unknown report id is 404.
   *
   * A failure before the first byte is a normal JSON error without the
   * attachment header. A failure after the file has started streaming
   * aborts the response, so the browser sees a failed download rather
   * than a truncated file.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `downloadReport$VndOpenxmlformatsOfficedocumentSpreadsheetmlSheet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadReport$VndOpenxmlformatsOfficedocumentSpreadsheetmlSheet(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;

    /**
     * The report&#x27;s period: &#x60;YYYY-MM&#x60; for a &#x60;month&#x60; report, &#x60;YYYY-MM-DD&#x60;
     * for a &#x60;date&#x60; report, absent for a &#x60;none&#x60; report.
     */
    period?: string;
    context?: HttpContext
  }
): Observable<Blob> {

    return this.downloadReport$VndOpenxmlformatsOfficedocumentSpreadsheetmlSheet$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Path part for operation listReportRecipients
   */
  static readonly ListReportRecipientsPath = '/reports/{report_id}/recipients';

  /**
   * List a scheduled report's email recipients.
   *
   * Returns the recipients of a scheduled report, in the order they were
   * saved. An unknown report id, or a report that is not scheduled
   * (`laskut`, `laskut-kaikki-tiedot`, which are never emailed), is 404.
   *
   * Requires the `reports:manage-recipients` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listReportRecipients()` instead.
   *
   * This method doesn't expect any request body.
   */
  listReportRecipients$Response(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ReportRecipientList>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.ListReportRecipientsPath, 'get');
    if (params) {
      rb.path('report_id', params.report_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportRecipientList>;
      })
    );
  }

  /**
   * List a scheduled report's email recipients.
   *
   * Returns the recipients of a scheduled report, in the order they were
   * saved. An unknown report id, or a report that is not scheduled
   * (`laskut`, `laskut-kaikki-tiedot`, which are never emailed), is 404.
   *
   * Requires the `reports:manage-recipients` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listReportRecipients$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listReportRecipients(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;
    context?: HttpContext
  }
): Observable<ReportRecipientList> {

    return this.listReportRecipients$Response(params).pipe(
      map((r: StrictHttpResponse<ReportRecipientList>) => r.body as ReportRecipientList)
    );
  }

  /**
   * Path part for operation replaceReportRecipients
   */
  static readonly ReplaceReportRecipientsPath = '/reports/{report_id}/recipients';

  /**
   * Replace a scheduled report's email recipients.
   *
   * Replaces the report's whole recipient list in one transaction and
   * returns the stored list. An empty list clears the recipients (the
   * schedule then archives only and raises a delivery task).
   *
   * Every address must be a single plain address (no display name) whose
   * domain, compared case-insensitively, is exactly one of the
   * deploy-configured allowed recipient domains; subdomains do not
   * match. If any address fails that check, nothing is stored and the
   * response is 422 naming every rejected address. A structurally
   * invalid body (a missing email, an unknown kind, the same address
   * twice compared case-insensitively) is 400. An unknown or unscheduled
   * report id is 404.
   *
   * Requires the `reports:manage-recipients` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `replaceReportRecipients()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceReportRecipients$Response(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;
    context?: HttpContext
    body: ReportRecipientList
  }
): Observable<StrictHttpResponse<ReportRecipientList>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.ReplaceReportRecipientsPath, 'put');
    if (params) {
      rb.path('report_id', params.report_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportRecipientList>;
      })
    );
  }

  /**
   * Replace a scheduled report's email recipients.
   *
   * Replaces the report's whole recipient list in one transaction and
   * returns the stored list. An empty list clears the recipients (the
   * schedule then archives only and raises a delivery task).
   *
   * Every address must be a single plain address (no display name) whose
   * domain, compared case-insensitively, is exactly one of the
   * deploy-configured allowed recipient domains; subdomains do not
   * match. If any address fails that check, nothing is stored and the
   * response is 422 naming every rejected address. A structurally
   * invalid body (a missing email, an unknown kind, the same address
   * twice compared case-insensitively) is 400. An unknown or unscheduled
   * report id is 404.
   *
   * Requires the `reports:manage-recipients` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `replaceReportRecipients$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  replaceReportRecipients(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;
    context?: HttpContext
    body: ReportRecipientList
  }
): Observable<ReportRecipientList> {

    return this.replaceReportRecipients$Response(params).pipe(
      map((r: StrictHttpResponse<ReportRecipientList>) => r.body as ReportRecipientList)
    );
  }

  /**
   * Path part for operation startReportTestRun
   */
  static readonly StartReportTestRunPath = '/reports/{report_id}/test-runs';

  /**
   * Email a test run of a scheduled report to the caller.
   *
   * Starts a `ReportRunWorkflow` for the report's default period whose
   * only recipient is the caller's own email address (from the users
   * table). The email is the scheduled one with its subject prefixed
   * `[TEST] `; it goes to no stored recipient, is not archived and
   * creates no operational task on failure. The response only says the
   * run started.
   *
   * The caller's address is subject to the recipient domain allowlist:
   * a caller with no email address, or one outside the allowlist, is
   * 422. An unknown or unscheduled report id is 404.
   *
   * Requires the `reports:manage-recipients` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `startReportTestRun()` instead.
   *
   * This method doesn't expect any request body.
   */
  startReportTestRun$Response(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ReportTestRunAccepted>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.StartReportTestRunPath, 'post');
    if (params) {
      rb.path('report_id', params.report_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportTestRunAccepted>;
      })
    );
  }

  /**
   * Email a test run of a scheduled report to the caller.
   *
   * Starts a `ReportRunWorkflow` for the report's default period whose
   * only recipient is the caller's own email address (from the users
   * table). The email is the scheduled one with its subject prefixed
   * `[TEST] `; it goes to no stored recipient, is not archived and
   * creates no operational task on failure. The response only says the
   * run started.
   *
   * The caller's address is subject to the recipient domain allowlist:
   * a caller with no email address, or one outside the allowlist, is
   * 422. An unknown or unscheduled report id is 404.
   *
   * Requires the `reports:manage-recipients` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `startReportTestRun$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  startReportTestRun(params: {

    /**
     * The report id (see &#x60;Report.id&#x60;).
     */
    report_id: string;
    context?: HttpContext
  }
): Observable<ReportTestRunAccepted> {

    return this.startReportTestRun$Response(params).pipe(
      map((r: StrictHttpResponse<ReportTestRunAccepted>) => r.body as ReportTestRunAccepted)
    );
  }

  /**
   * Path part for operation listReportRuns
   */
  static readonly ListReportRunsPath = '/report-runs';

  /**
   * List archived scheduled report runs, newest first.
   *
   * Returns the archive of scheduled runs ordered by `started_at`
   * descending (ties by id descending), optionally for one report.
   * Pages with `limit` and the opaque `after` cursor taken from the
   * previous page's `next`; `next` is absent on the last page. Test runs
   * are never archived. An unknown `report_id`, or a malformed cursor or
   * limit, is 400.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listReportRuns()` instead.
   *
   * This method doesn't expect any request body.
   */
  listReportRuns$Response(params?: {

    /**
     * Only runs of this report.
     */
    report_id?: string;
    limit?: number;

    /**
     * The &#x60;next&#x60; cursor of the previous page.
     */
    after?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ReportRunList>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.ListReportRunsPath, 'get');
    if (params) {
      rb.query('report_id', params.report_id, {});
      rb.query('limit', params.limit, {});
      rb.query('after', params.after, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ReportRunList>;
      })
    );
  }

  /**
   * List archived scheduled report runs, newest first.
   *
   * Returns the archive of scheduled runs ordered by `started_at`
   * descending (ties by id descending), optionally for one report.
   * Pages with `limit` and the opaque `after` cursor taken from the
   * previous page's `next`; `next` is absent on the last page. Test runs
   * are never archived. An unknown `report_id`, or a malformed cursor or
   * limit, is 400.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listReportRuns$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listReportRuns(params?: {

    /**
     * Only runs of this report.
     */
    report_id?: string;
    limit?: number;

    /**
     * The &#x60;next&#x60; cursor of the previous page.
     */
    after?: string;
    context?: HttpContext
  }
): Observable<ReportRunList> {

    return this.listReportRuns$Response(params).pipe(
      map((r: StrictHttpResponse<ReportRunList>) => r.body as ReportRunList)
    );
  }

  /**
   * Path part for operation downloadReportRun
   */
  static readonly DownloadReportRunPath = '/report-runs/{run_id}/download';

  /**
   * Redirect to a short-lived signed URL for an archived report file.
   *
   * Returns HTTP 302 with `Location` set to a 5-minute signed URL for the
   * archived file, byte-identical to the emailed attachment and carrying
   * the run's `file_name`. An unknown run is 404; a run with no archived
   * file (`downloadable` false: status `generating` or `failed`) is 409.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadReportRun()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadReportRun$Response(params: {
    run_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Error>> {

    const rb = new RequestBuilder(this.rootUrl, ReportsService.DownloadReportRunPath, 'get');
    if (params) {
      rb.path('run_id', params.run_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Error>;
      })
    );
  }

  /**
   * Redirect to a short-lived signed URL for an archived report file.
   *
   * Returns HTTP 302 with `Location` set to a 5-minute signed URL for the
   * archived file, byte-identical to the emailed attachment and carrying
   * the run's `file_name`. An unknown run is 404; a run with no archived
   * file (`downloadable` false: status `generating` or `failed`) is 409.
   *
   * Requires the `reports:download` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `downloadReportRun$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadReportRun(params: {
    run_id: number;
    context?: HttpContext
  }
): Observable<Error> {

    return this.downloadReportRun$Response(params).pipe(
      map((r: StrictHttpResponse<Error>) => r.body as Error)
    );
  }

}
